import { useEffect, useState } from "react";
import { Mail, Check, ShieldCheck, ArrowRight, Sparkles, BellRing, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  gmailConnect, gmailStatus, recordSubscriberEvent,
  joinGmailWaitlist, fetchGmailWaitlistMe,
} from "@/lib/api";
import { toast } from "sonner";

export const CONNECT_GMAIL = {
  page: "connect-gmail-page",
  connectBtn: "connect-gmail-btn",
  waitlistBtn: "gmail-waitlist-btn",
  waitlistSuccess: "gmail-waitlist-success",
};

const BENEFITS = [
  { title: "Automatic deadline tracking", desc: "Permission slips, fees, registration cutoffs — surfaced before they bite." },
  { title: "Automatic event creation",     desc: "Soccer practice change at 4 PM? It's already on your calendar by 4:05." },
  { title: "Automatic task generation",    desc: "AI breaks every email into the actual to-dos a parent owns." },
  { title: "Less manual data entry",       desc: "Stop forwarding emails to yourself. KidHQ reads the family inbox for you." },
];

export default function ConnectGmailPage() {
  const [status, setStatus] = useState(null);
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    recordSubscriberEvent("gmail_onboarding_viewed").catch(() => {});
    gmailStatus().then(setStatus).catch(() => setStatus({ connected: false, available: false }));
    fetchGmailWaitlistMe().then(d => setOnWaitlist(Boolean(d?.on_waitlist))).catch(() => {});
  }, []);

  const gmailAvailable = status?.configured === true || status?.available === true;

  const handleConnect = async () => {
    try {
      const r = await gmailConnect();
      const url = r?.authorize_url || r?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Gmail OAuth is not available yet.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 503) {
        toast.message("Gmail OAuth credentials not yet configured. We'll let you know when it's ready.");
      } else {
        toast.error(detail || "Could not start Gmail connection.");
      }
    }
  };

  const handleJoinWaitlist = async () => {
    if (joining || onWaitlist) return;
    setJoining(true);
    try {
      await joinGmailWaitlist("connect_gmail_page");
      setOnWaitlist(true);
      toast.success("You're on the list. We'll notify you as soon as Gmail integration becomes available.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join the waitlist. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div data-testid={CONNECT_GMAIL.page} className="space-y-10">
      <header className="fcc-card overflow-hidden grid lg:grid-cols-12">
        <div className="lg:col-span-8 p-8 sm:p-10">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="fcc-pill fcc-chip-emma"><Mail className="w-3 h-3" /> Gmail Intelligence</span>
            {!gmailAvailable && (
              <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">Launching soon — join the waitlist</span>
            )}
          </div>
          <h1 className="font-display text-[40px] sm:text-[52px] leading-[1.05] text-[var(--fcc-ink)] mt-6">
            Connect Gmail and Let KidHQ Do the Work
          </h1>
          <p className="text-[var(--fcc-ink-soft)] mt-5 max-w-2xl leading-relaxed">
            Connect Gmail to automatically organize school, sports, camp, activity, and payment emails.
            KidHQ reads them, classifies them, and turns them into deadlines, events, and parent tasks — so you don&apos;t have to.
          </p>

          <div className="mt-7 flex flex-wrap gap-3 items-center">
            {gmailAvailable ? (
              <button
                data-testid={CONNECT_GMAIL.connectBtn}
                onClick={handleConnect}
                className="fcc-btn-primary"
              >
                <Mail className="w-4 h-4" /> Connect Gmail
              </button>
            ) : onWaitlist ? (
              <div
                data-testid={CONNECT_GMAIL.waitlistSuccess}
                className="flex items-start gap-3 px-5 py-3 rounded-xl bg-[var(--fcc-green)]/10 border border-[var(--fcc-green)]/30 text-[var(--fcc-green)]"
              >
                <Check className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="text-sm leading-relaxed">
                  <span className="font-semibold">You&apos;re on the list.</span>{" "}
                  <span className="text-[var(--fcc-ink-soft)]">We&apos;ll notify you as soon as Gmail integration becomes available.</span>
                </p>
              </div>
            ) : (
              <button
                data-testid={CONNECT_GMAIL.waitlistBtn}
                onClick={handleJoinWaitlist}
                disabled={joining}
                className="fcc-btn-primary disabled:opacity-60"
              >
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
                Join Gmail Waitlist
              </button>
            )}
            <Link to="/app" className="text-sm text-[var(--fcc-muted)] hover:underline">
              Maybe later <ArrowRight className="inline w-3 h-3" />
            </Link>
          </div>
        </div>

        <aside className="lg:col-span-4 p-7 sm:p-8 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)]">
          <p className="label-overline flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Privacy promise</p>
          <p className="text-sm text-[var(--fcc-ink-soft)] mt-3">
            KidHQ only reads child-related messages (school, sports, camps, medical). We never read personal mail, never sell data, and you can revoke access in one click.
          </p>
        </aside>
      </header>

      <section>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-5 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[var(--fcc-green)]" /> What you unlock
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          {BENEFITS.map(b => (
            <article key={b.title} className="fcc-card p-6">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[var(--fcc-green)] mt-1 shrink-0" />
                <div>
                  <h3 className="font-display text-xl text-[var(--fcc-ink)]">{b.title}</h3>
                  <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
